import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, Home } from "lucide-react";

export default function AdminLogin() {
  const { user, login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Already signed in: show a clear choice instead of silently redirecting
  // (prevents the "I can't reach the login form" dead-end with a stale session).
  if (user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex items-center justify-center px-6" data-testid="admin-already-signed-in">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#B8860B] font-bold mb-3">
            <Lock size={11} /> Restricted · Back Office
          </div>
          <h1 className="text-3xl font-serif tracking-tight">Treasury House</h1>
          <p className="text-[#8A8A8A] text-sm mt-2">You're already signed in as <span className="text-[#ECECE4]">{user.email}</span>.</p>
          <div className="mt-6 flex flex-col gap-3">
            <button onClick={() => nav("/admin", { replace: true })} data-testid="admin-goto-dashboard"
              className="w-full bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm">
              Go to Dashboard
            </button>
            <button onClick={async () => { await logout(); }} data-testid="admin-signout-relogin"
              className="w-full bg-transparent border border-[#2A2A22] hover:border-[#B8860B] text-[#ECECE4] uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm">
              Sign out &amp; log in as someone else
            </button>
            <Link to="/" className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4] mt-2">Return to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/admin", { replace: true });
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(" ") : (ex.message || "Login failed")));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#B8860B] font-bold mb-3">
            <Lock size={11} /> Restricted · Back Office
          </div>
          <h1 className="text-3xl font-serif tracking-tight">Treasury House</h1>
          <p className="text-[#8A8A8A] text-sm mt-1">Admin Back-Office Sign In</p>
          <Link to="/" data-testid="admin-login-home-link" className="inline-flex items-center gap-1.5 mt-4 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4] transition-colors"><Home size={12} /> Return to Home</Link>
        </div>

        <form onSubmit={submit} className="bg-[#13130E] border border-[#2A2A22] rounded-md p-7 shadow-2xl">
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] block mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none transition-colors"
            data-testid="admin-login-email"
          />

          <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] block mt-4 mb-1.5">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none transition-colors"
            data-testid="admin-login-password"
          />

          {err && (
            <div className="mt-3 p-2 rounded-sm bg-[#9E2A2B]/15 border border-[#9E2A2B]/40 text-[#FF8585] text-[12px]" data-testid="admin-login-error">
              {err}
            </div>
          )}

          <div className="mt-3 text-right">
            <Link to="/admin/forgot-password" data-testid="admin-forgot-link" className="text-[11px] text-[#8A8A8A] hover:text-[#E4C97A] transition-colors">Forgot password?</Link>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="mt-5 w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2"
            data-testid="admin-login-submit"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Authenticating…</> : "Sign In"}
          </button>

          <div className="mt-5 pt-4 border-t border-[#2A2A22] text-[10px] text-[#5C5C5C] leading-relaxed">
            Single-admin platform. Access tokens for counterparties are issued from the Back Office after sign-in.
          </div>
        </form>
      </div>
    </div>
  );
}
